<template>
    <div v-if="sessionActive">

        <v-container fluid class="pa-3">

            <v-alert v-if="!hasPermission('view projects')"
                     class="body-2"
                     type="error"
            >
                Your user account doesn't have permission to view projects
            </v-alert>

            <v-sheet class="elevation-5" v-if="hasPermission('view projects')">
                <material-header title="Projects"
                                 info="Create, manage and track progress"
                                 help="projects"
                                 icon="mdi-folder-clock-outline"
                                 class="elevation-5">

                    <template slot="buttons">

                        <v-tooltip color="primary" top>
                            <template v-slot:activator="{ on }">
                                <v-btn :disabled="!hasPermission('view projects')"
                                       @click="openCalendar()"
                                       v-on="on"
                                       icon>
                                    <v-icon>mdi-calendar-today</v-icon>
                                </v-btn>
                            </template>
                            <span>Calendar view</span>
                        </v-tooltip>

                        <v-tooltip :color="hasPermission('create projects') ? 'primary' : 'red'" top>
                            <template v-slot:activator="{ on }">
                                <v-btn :disabled="!hasPermission('create projects')"
                                       v-on="on"
                                       fab
                                       small
                                       color="primary"
                                       @click="newProject()">
                                    <v-icon>mdi-plus</v-icon>
                                </v-btn>
                            </template>
                            <span v-if="hasPermission('create projects')">Create new project</span>
                            <span v-else>You are not authorized to create new projects</span>
                        </v-tooltip>

                    </template>

                </material-header>

                <!-- TABS -->
                <v-tabs slider-color="primary" color="white" v-model="activeTab" class="pb-1">
                    <v-tab class="ml-3" key="active">
                        Active
                    </v-tab>
                    <v-tab key="archived">
                        Archived
                    </v-tab>
                </v-tabs>

                <v-tabs-items v-model="activeTab" v-if="hasPermission('view projects')">

                    <!-- ACTIVE TAB -->
                    <v-tab-item key="active">
                        <active-table
                            class="pt-3"
                            :projects="projects"
                            :loading="projectsLoading"
                            @edit="editProject"
                            @delete="deleteProjectDo"
                            @clone="cloneProjectDo"
                            @stats="openProjectStats"
                            @quotas="openQuotaStats"
                            @geostats="openProjectGeoStats"
                            @participants="openParticipants"
                            @disqualify="openDisqualify"
                            @refresh="refresh"
                        />
                    </v-tab-item>

                    <!-- ARCHIVED TAB -->
                    <v-tab-item key="archived">
                        <archived-table
                            @edit="editProject"
                            @delete="deleteProjectDo"
                            @clone="cloneProjectDo"
                            @stats="openProjectStats"
                        />
                    </v-tab-item>

                </v-tabs-items>
            </v-sheet>
        </v-container>

        <!-- PROJECT STATS DIALOG -->
        <v-dialog v-if="projectStatsOpenModel"
                  v-model="projectStatsOpenModel"
                  fullscreen
                  hide-overlay
                  transition="fade-transition"
        >
            <stat-boxes :project="project"
                        @close="closeProjectStats"
            ></stat-boxes>

        </v-dialog>

        <!-- PROJECT GEO STATS DIALOG -->
        <v-dialog v-if="projectGeoStatsModel"
                  v-model="projectGeoStatsModel"
                  fullscreen
                  hide-overlay
                  transition="fade-transition"
        >
            <geo-stats :project="project"
                       @close="closeGeoStats"
            ></geo-stats>

        </v-dialog>

        <!-- PROJECTS CALENDAR VIEW DIALOG -->
        <v-dialog v-if="projectsCalendarDialogModel"
                  v-model="projectsCalendarDialogModel"
                  fullscreen
                  hide-overlay
                  transition="fade-transition"
        >
            <calendar-view @edit="editProject" :projects="projects" @close="closeCalendar"/>

        </v-dialog>

        <!-- PARTICIPANT DIALOG -->
        <participants-dialog v-if="project"
                             v-model="projectParticipantsDialogModel"
                             :project="project"
                             :key="'participants_'+project.id"
        ></participants-dialog>

        <!-- DIAQUALIFY DIALOG -->
        <disqualify-dialog v-if="project"
                           v-model="disqualifyParticipantsDialogModel"
                           :project="project"
                           :key="'disqualify_'+project.id"
        ></disqualify-dialog>
    </div>
</template>

<script>

import {mapActions, mapGetters, mapMutations, mapState} from 'vuex';
import EditDialog from './EditProject/EditProject';
import activeSessionChecker from '../../../components/mixins/activeSessionCheckerMixin';
import MaterialHeader from '../../../components/UI/MaterialHeader';
import StatBoxes from './Dashboard/ProjectDashboard';
import storeMixin from './StoreMixin';
import CalendarView from './CalendarView';
import ActiveTable from './ListProjectsActiveTable';
import ArchivedTable from './ListProjectsArchivedTable';
import GeoStats from "./Dashboard/GeoStats";
import ParticipantsDialog from "./ParticipantsDialog";
import DisqualifyDialog from "./DisqualifyDialog";
import ToolbarPage from "../../../components/UI/ToolbarPage";

export default {
    components: {
        ToolbarPage,
        EditDialog,
        MaterialHeader,
        StatBoxes,
        CalendarView,
        ActiveTable,
        ArchivedTable,
        GeoStats,
        ParticipantsDialog,
        DisqualifyDialog
    },

    mixins: [activeSessionChecker, storeMixin],

    data() {
        return {
            projectStatsOpenModel: false,
            projectGeoStatsModel: false,
            project: null,
            projectsCalendarDialogModel: false,
            projectParticipantsDialogModel: false,
            disqualifyParticipantsDialogModel: false,
            projectQuotaStatsModel: false,
            intervalTimer: null,
        }
    },

    computed: {

        ...mapState('ProjectManager', [
            'projects',
            'projectsLoading',
            'activeTabModel'
        ]),

        ...mapGetters('ProjectManager', [
            'getFilteredProjects'
        ]),

        activeTab: {
            get() {
                return this.activeTabModel;
            },
            set(value) {
                this.setActiveTab(value);
            }
        }
    },

    watch: {
        $route(to, from) {
            if (to.name === 'project-manager-list') {
                this.refresh(true, false);
            }
        }
    },

    methods: {

        ...mapMutations('ProjectManager', [
            'setActiveTab'
        ]),

        ...mapActions('ProjectManager', [
            'loadProjects',
            'createProject',
            'updateProject',
            'deleteProject',
            'cloneProject',
        ]),

        newProject() {
            this.$router.push({name: 'project-manager-create'});
        },

        refresh(force = true, showNotification = true) {

            this.loadProjects({force: force})
                .then(() => {
                    if (force && showNotification) {
                        Bus.$emit('flash-message', {message: 'Projects updated...'});
                    }
                })
                .catch(({response}) => {
                    Bus.$emit('server-error', {message: 'Unable to update projects!'});
                });
        },

        editProject(project) {
            if (this.hasPermission('update projects')) {

                if (Array.isArray(project.json_query)) project.json_query = {};

                this.$router.push({name: 'project-manager-edit', params: {id: project.id, project: project}});
            }
        },

        deleteProjectDo(id) {

            this.deleteProject(id)
                .then(() => {
                    Bus.$emit('flash-message', {message: 'Project deleted...'});
                })
                .catch(({response}) => {

                    if (response.status === 409) {
                        Bus.$emit('server-error', {message: response.data.message});
                    } else {
                        Bus.$emit('server-error', {message: 'Project failed to delete'});
                    }
                });
        },

        cloneProjectDo(id) {

            this.cloneProject(id)
                .then(() => {
                    Bus.$emit('flash-message', {message: 'Project cloned...'});
                })
                .catch(({response}) => {

                    if (response.status === 409) {
                        Bus.$emit('server-error', {message: response.data.message});
                    } else {
                        Bus.$emit('server-error', {message: 'Project failed to clone'});
                    }
                });
        },

        openCalendar() {
            this.projectsCalendarDialogModel = true;
        },

        closeCalendar() {
            this.projectsCalendarDialogModel = false;
        },

        openProjectStats(project) {
            this.project = project;
            this.projectStatsOpenModel = true;
        },

        openQuotaStats(project) {
            this.project = project;
            this.projectQuotaStatsModel = true;
        },

        openProjectGeoStats(project) {
            this.project = project;
            this.projectGeoStatsModel = true;
        },

        openParticipants(project) {
            this.project = project;
            this.projectParticipantsDialogModel = true;
        },

        openDisqualify(project) {
            this.project = project;
            this.disqualifyParticipantsDialogModel = true;
        },

        closeGeoStats() {
            this.project = null;
            this.projectGeoStatsModel = false;
        },

        closeProjectStats() {
            this.project = null;
            this.projectStatsOpenModel = false;
        },

        closeQuotaStats() {
            this.project = null;
            this.projectQuotaStatsModel = false;
        },
    },

    mounted() {
        if (this.intervalTimer === null) {
            this.intervalTimer = setInterval(
                () => this.refresh(false),
                60000
            );
        }
    },
}

</script>

<style scoped>
.overflow {
    overflow: hidden;
    height: 19px;
    width: 100%;
}

.deadline {
    width: 80px;
}

</style>
